import { useOasGraphLazy } from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { Box, Paragraph10 } from '@squareup/dex-ui-shared-base';
import { MarkdownCodeBlock } from '@squareup/dex-ui-shared-markdown-components';
import React, { FC, useMemo } from 'react';

import {
  parseEndpointTag,
  specOptionsFor,
} from './endpoint-request-snippet-utils';

interface MdxEndpointResponseSnippetProps {
  // API version. Same shape as the Request variant — "latest" or a
  // published square_version like "2025-09-24".
  api?: string;
  // HTTP verb + path, e.g. "GET /v2/channels". Parsed the same way as
  // the Request variant; matched against `endpoint.httpMethod + path`.
  endpoint: string;
  // Optional scenario name. If unset (or unmatched), falls back to the
  // first scenario, then to `endpoint.responseBody.example` if available.
  example?: string;
}

// Render the example RESPONSE body for the requested endpoint+scenario as
// a JSON code block. Distinct from <EndpointRequestSnippet>, which renders
// the multi-language SDK CALL using `MarkdownIgnitionCodeBlock` — there's
// no SDK-language variation for response examples, so we just emit JSON.
const MdxEndpointResponseSnippet: FC<MdxEndpointResponseSnippetProps> = ({
  api,
  endpoint: endpointTag,
  example,
}) => {
  const parsed = useMemo(() => parseEndpointTag(endpointTag), [endpointTag]);
  const specOptions = useMemo(() => specOptionsFor(api), [api]);
  const { graph } = useOasGraphLazy(specOptions, getDeveloperApi());

  const responseBody = useMemo(() => {
    if (!graph || !parsed) {
      return null;
    }
    const match = graph
      .listEndpoints()
      .find(
        (e) =>
          e.httpMethod.toUpperCase() === parsed.method && e.path === parsed.path
      );
    if (!match) {
      return null;
    }
    // Scenario fallback chain mirrors the Request adapter: named scenario,
    // then first scenario, then the endpoint's default `responseBody.example`.
    const scenarios = match.exampleScenarios || [];
    const named = example && scenarios.find((s) => s.name === example);
    const scenario = named || scenarios[0];
    if (scenario?.response) {
      return scenario.response;
    }
    const fallbackExample = match.responseBody?.example;
    if (fallbackExample === undefined || fallbackExample === null) {
      return null;
    }
    if (typeof fallbackExample === 'string') {
      try {
        return JSON.parse(fallbackExample);
      } catch {
        return fallbackExample;
      }
    }
    // Already-parsed object/array from the OAS graph — return as-is so
    // the renderer can JSON.stringify it. Without this branch, anything
    // non-string fell through to `return null` and the page rendered an
    // empty `{}` instead of the endpoint's default example.
    return fallbackExample;
  }, [graph, parsed, example]);

  if (!parsed) {
    return (
      <Box>
        <Paragraph10>
          Could not parse endpoint: <code>{endpointTag}</code>
        </Paragraph10>
      </Box>
    );
  }

  // While the graph is loading, render an empty json block. Once the
  // response lands, swap in the pretty-printed body.
  const content =
    responseBody === undefined || responseBody === null
      ? '{}'
      : typeof responseBody === 'string'
      ? responseBody
      : JSON.stringify(responseBody, null, 2);

  return <MarkdownCodeBlock content={content} language="json" />;
};

MdxEndpointResponseSnippet.displayName = 'MdxEndpointResponseSnippet';

export { MdxEndpointResponseSnippet };
